import React from 'react';
import Tooltip from '../ui/Tooltip';
import './TaskPriorityBars.css';

/**
 * Priority as three upright bars of rising height, 1 / 2 / 3 lit from the
 * left — Linear's signal, in one muted colour like Linear
 * (card ef074711, owner 01.10.2026). It replaces the coloured dot, which
 * looked like the status circle beside it: two circles, two meanings.
 *
 * The fill count IS the level, so the scale reads without colour; the app
 * tooltip and the aria-label name it.
 */

export const PRIORITY_LEVELS = ['low', 'medium', 'high'];

const LABELS = {
  low: 'Low priority',
  medium: 'Medium priority',
  high: 'High priority',
};

export function priorityLabel(priority) {
  return LABELS[priority] || LABELS.medium;
}

// Three upright bars of rising height, bottom-aligned (Boards Redesign Main /
// Card anatomy artboards): 3 px wide, 5 / 8 / 11 px tall, 1.5 px apart.
const BARS = [
  { n: 1, x: 2, h: 5 },
  { n: 2, x: 6.5, h: 8 },
  { n: 3, x: 11, h: 11 },
];
const BASELINE = 13.5;

/** The bars alone (also the leading icon in the task modal's picker). */
export function PriorityBarsIcon({ priority = 'medium' }) {
  const level = PRIORITY_LEVELS.includes(priority) ? priority : 'medium';
  const filled = PRIORITY_LEVELS.indexOf(level) + 1;
  // Left to right: bar 1, 2, 3. A bar is lit when its number <= the level.
  return (
    <svg className={`task-priority-bars task-priority-bars--${level}`} width="16" height="16" viewBox="0 0 16 16" aria-hidden focusable="false">
      {BARS.map(({ n, x, h }) => (
        <rect
          key={n}
          x={x}
          y={BASELINE - h}
          width="3"
          height={h}
          rx="1"
          className={n <= filled ? 'task-priority-bar--on' : 'task-priority-bar--off'}
        />
      ))}
    </svg>
  );
}

export default function TaskPriorityBars({ priority = 'medium', onCycle, className = '' }) {
  const level = PRIORITY_LEVELS.includes(priority) ? priority : 'medium';
  const label = priorityLabel(level);

  // Read-only when there is nothing to cycle — the chat share card renders one
  // inside its own <button>, and a button inside a button is invalid markup.
  if (!onCycle) {
    return (
      <span className={`task-priority ${className}`.trim()} role="img" aria-label={label} data-priority={level}>
        <PriorityBarsIcon priority={level} />
      </span>
    );
  }

  return (
    <Tooltip content={`${label} — click to change`} side="top">
      <button
        type="button"
        className={`task-priority ${className}`.trim()}
        aria-label={`${label}. Click to change.`}
        data-priority={level}
        onClick={(e) => {
          e.stopPropagation();
          onCycle(PRIORITY_LEVELS[(PRIORITY_LEVELS.indexOf(level) + 1) % PRIORITY_LEVELS.length]);
        }}
        // The card is a drag handle and dnd-kit arms on pointerdown.
        onPointerDown={(e) => e.stopPropagation()}
      >
        <PriorityBarsIcon priority={level} />
      </button>
    </Tooltip>
  );
}
